import { TeammateFormFields, TeammateRole } from '@/types/teammates';
import {
  SVAutocomplete,
  SVEmailInput,
  SVForm,
  SVPhoneInput,
  SVSelect,
  SVTextInput,
} from '../../core';
import { Card, Grid, Stack, Typography } from '@mui/material';
import {
  useAddTeammate,
  useEditTeammate,
  useGetTeammateListForSelection,
} from '@/hooks/requests/useTeammates';
import { StatusQueryParam } from '@/types/common';
import FormSubmissionButtons from '../../FormSubmissionButtons';
import { useGetUser } from '@/hooks/requests/useUser';
import { UserRoles } from '@/types/user';
import { useGetCompanyListForSelection } from '@/hooks/requests/useCompanies';
import { useEffect } from 'react';
import ProfileEditAvatarButton from '@/components/profile/ProfileEditAvatarButton';
import { MESSAGES } from '@/constants/messages';
import showToast from '@/utils/toast';
import { useForm } from 'react-hook-form';
import SVPersonalBankingLinkField from '@/components/core/inputs/PersonalBankingLinkField';
import { initalTeammateForm } from '@/constants/teammates';

type TeammateFormProps = {
  isEdit?: boolean;
  advisorUrl?: string;
  defaultValues?: TeammateFormFields;
  onLeave?: () => void;
  onSuccess: (uuid: string) => void;
};

export default function TeammateForm({
  isEdit,
  advisorUrl,
  defaultValues,
  onLeave,
  onSuccess,
}: TeammateFormProps) {
  const { user } = useGetUser();
  const { addTeammate, isAddTeammatePending } = useAddTeammate();
  const { editTeammate, isEditTeammatePending } = useEditTeammate();
  const isPending = isAddTeammatePending || isEditTeammatePending;

  const isAdvisorAdmin = user?.role === UserRoles.TEAMMATE_ADMIN;
  const { companiesList } = useGetCompanyListForSelection({}, true);

  const formMethods = useForm<TeammateFormFields>({
    defaultValues: defaultValues ? defaultValues : initalTeammateForm,
    mode: 'onBlur',
  });

  useEffect(() => {
    formMethods.reset();
  }, []);

  const [uuid, companyId, selectedRole, alias] = formMethods.watch([
    'uuid',
    'companyId',
    'role',
    'alias',
  ]);

  const { teammatesList, isTeammatesListPending } =
    useGetTeammateListForSelection(
      {
        company_id: companyId,
        status: StatusQueryParam.ACTIVE,
        roles:
          selectedRole === TeammateRole.SUPPORTER
            ? [TeammateRole.ADMIN, TeammateRole.ADVISOR]
            : [TeammateRole.SUPPORTER],
      },
      !!companyId,
    );

  const { isDirty } = formMethods.formState;

  useEffect(() => {
    if (!isDirty) {
      return;
    }

    formMethods.setValue('members', []);
    formMethods.clearErrors('members');
  }, [companyId, selectedRole, isDirty]);

  const shouldShowSupportMembers =
    selectedRole === TeammateRole.ADMIN ||
    selectedRole === TeammateRole.ADVISOR;
  const shouldShowAdminAdvisor = selectedRole === TeammateRole.SUPPORTER;

  async function handleSubmit(data: TeammateFormFields) {
    try {
      if (isEdit) {
        await editTeammate(data);
        showToast({
          type: 'success',
          data: {
            content: 'Teammate has been successfully edited',
          },
        });
        onSuccess(data.uuid);
      } else {
        const response = await addTeammate(data);
        showToast({
          type: 'success',
          data: {
            content: 'Teammate has been successfully added',
          },
        });
        onSuccess(response);
      }
    } catch (error: any) {
      showToast({
        type: 'error',
        data: {
          content: error?.response?.data
            ? error.response.data
            : MESSAGES.genericError,
        },
      });
    }
  }

  return (
    <SVForm<TeammateFormFields> methods={formMethods} onSubmit={handleSubmit}>
      <Stack spacing={2}>
        <Card>
          <Typography variant="h5" mb={2}>
            Basic Info
          </Typography>
          <Grid spacing={2} container>
            <Grid container size={{ xs: 12, md: 9 }}>
              <Grid size={{ xs: 12, md: 4 }}>
                <SVTextInput
                  name="firstName"
                  label="First Name"
                  rules={{ required: true }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <SVTextInput
                  name="lastName"
                  label="Last Name"
                  rules={{ required: true }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <SVEmailInput name="email" label="Email" />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <SVSelect
                  name="role"
                  label="Role"
                  options={[
                    {
                      label: 'Admin',
                      value: TeammateRole.ADMIN,
                    },
                    {
                      label: 'Advisor',
                      value: TeammateRole.ADVISOR,
                    },
                    {
                      label: 'Support',
                      value: TeammateRole.SUPPORTER,
                    },
                  ]}
                  rules={{ required: true }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <SVTextInput
                  name="title"
                  label="Title"
                  rules={{ required: true }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <SVTextInput
                  name="branchOfficeName"
                  label="Branch Office Name (Optional)"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <SVPhoneInput
                  name="cellPhoneNumber"
                  label="Cell Phone Number"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <SVPhoneInput
                  name="businessPhoneNumber"
                  label="Business Phone Number"
                />
              </Grid>
              {!isAdvisorAdmin && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <SVAutocomplete
                    name="companyId"
                    label="Company"
                    options={companiesList}
                    rules={{ required: true }}
                  />
                </Grid>
              )}
              {(shouldShowSupportMembers || shouldShowAdminAdvisor) && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <SVAutocomplete
                    name="members"
                    label={
                      selectedRole === TeammateRole.SUPPORTER
                        ? 'Admin/Advisor Teammates'
                        : 'Support Members (Optional)'
                    }
                    options={teammatesList}
                    multiple
                    disabled={!companyId || isTeammatesListPending}
                    rules={{
                      required: selectedRole === TeammateRole.SUPPORTER,
                    }}
                  />
                </Grid>
              )}
              {isEdit && (
                <Grid size={{ xs: 12 }}>
                  <SVPersonalBankingLinkField
                    name="alias"
                    label="Personal Banking Link"
                    advisorUrl={advisorUrl || ''}
                    alias={alias || ''}
                  />
                </Grid>
              )}
              <Grid size={{ xs: 12 }}>
                <SVTextInput
                  name="bio"
                  label="Bio (Optional)"
                  multiline
                  multilineRows={4}
                />
              </Grid>
            </Grid>
            <Grid sx={{ textAlign: 'center' }} size={{ xs: 12, md: 3 }}>
              <ProfileEditAvatarButton uuid={isEdit ? uuid : null} />
            </Grid>
          </Grid>
        </Card>
        <FormSubmissionButtons isLoading={isPending} onLeave={onLeave} />
      </Stack>
    </SVForm>
  );
}
